import React, { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MissionDetailSidebar from "../components/mission/MissionDetailSidebar";
import MissionInfoBar from "../components/mission/MissionInfoBar";
import MissionWeekSelector from "../components/mission/MissionWeekSelector";
import NavBarMission from "../components/navbars/NavBarMission";
import PageContainer from "../components/pageContainer/PageContainer";
import ModalCreateMission from "../components/modal/dataModal/data/modalMission/ModalCreateMission";
import ModalDeleteMission from "../components/modal/dataModal/data/modalMission/ModalDeleteMission";
import useTitle from "../hooks/TitleManager";
import { useMissionData } from "../hooks/useMissionData";

const Mission = () => {
  useTitle({ title: "Mission" });
  const navigate = useNavigate();
  const userHeaderRef = useRef(null);
  const [userHeaderHeightPx, setUserHeaderHeightPx] = useState(88);
  const [headerCompact, setHeaderCompact] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const {
    isWaitingMission,
    isWaitingUserMission,
    missionDetail,
    missionByWeek,
    currentWeekIndex,
    canGoPrev,
    canGoNext,
    goPrevWeek,
    goNextWeek,
    getWeekLabel,
    editTempWorkerPhone,
    editTempWorkerToken,
    getTotalWorkedHours,
    getTotalNightHours,
    getTotalOvertimes,
    timeCut,
    userMission,
    id,
  } = useMissionData();

  // Retrieve the list of missions for the current week
  const currentWeekMissions = missionByWeek[currentWeekIndex] || [];

  useLayoutEffect(() => {
    const el = userHeaderRef.current;
    if (!el) return;
    let raf = 0;
    const update = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        const h = el.getBoundingClientRect().height;
        if (h <= 0) return;
        const rounded = Math.round(h);
        setUserHeaderHeightPx((prev) =>
          Math.abs(prev - rounded) < 2 ? prev : rounded,
        );
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isWaitingUserMission]);

  return (
    <PageContainer type="mission" contentLayout="mission">
      <header
        ref={userHeaderRef}
        className={[
          "sticky top-0 z-30 shrink-0 bg-white px-4 py-3 sm:px-5 lg:px-[20px]",
          headerCompact ? "border-b border-gray-100" : "",
        ].join(" ")}
      >
        <NavBarMission
          user={userMission}
          isWaiting={isWaitingUserMission}
          onCompactChange={setHeaderCompact}
        />
      </header>

      {/* Pas de flex-1/min-h-0 ici : sinon la rangée est limitée à la hauteur du viewport et le sticky de la sidebar cesse de fonctionner correctement au scroll. */}
      <section className="flex shrink-0 flex-col gap-4 px-4 pb-8 pt-4 sm:px-5 md:gap-6 lg:px-[20px]">
        <div className="flex flex-col items-stretch gap-8 lg:flex-row lg:gap-12">
          <div className="flex w-full min-w-0 flex-col gap-6 self-start lg:max-w-[654px] lg:gap-8">
            <MissionInfoBar
              id={id}
              isWaitingUserMission={isWaitingUserMission}
              userMission={userMission}
              editTempWorkerPhone={editTempWorkerPhone}
              editTempWorkerToken={editTempWorkerToken}
            />

            <MissionWeekSelector
              isWaitingMission={isWaitingMission}
              missionByWeek={missionByWeek}
              currentWeekIndex={currentWeekIndex}
              currentWeekMissions={currentWeekMissions}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              goPrevWeek={goPrevWeek}
              goNextWeek={goNextWeek}
              getWeekLabel={getWeekLabel}
              timeCut={timeCut}
              weekStickyTopPx={userHeaderHeightPx}
            />
          </div>

          <MissionDetailSidebar
            missionDetail={missionDetail}
            currentWeekMissions={currentWeekMissions}
            getTotalWorkedHours={getTotalWorkedHours}
            getTotalNightHours={getTotalNightHours}
            getTotalOvertimes={getTotalOvertimes}
            sidebarStickyTopPx={userHeaderHeightPx}
            onEditMission={() => setEditOpen(true)}
            onDeleteMission={() => setDeleteOpen(true)}
          />
        </div>
      </section>

      <ModalCreateMission
        isOpen={editOpen}
        mission={missionDetail}
        companyId={missionDetail?.idCompany}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          window.location.reload();
        }}
      />
      <ModalDeleteMission
        isOpen={deleteOpen}
        mission={missionDetail}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          navigate("/hourly-tracking");
        }}
      />
    </PageContainer>
  );
};

export default Mission;
